import React from "react";
import { FaFilePdf, FaFileWord, FaFileImage, FaFileAlt, FaFileArchive } from "react-icons/fa";
import { Eye, Download, Trash2, Edit2 } from "lucide-react";

const getFileIcon = (type, name) => {
  const safeType = typeof type === 'string' ? type : '';
  const safeName = typeof name === 'string' ? name : '';
  if (safeType === "application/pdf") return <FaFilePdf className="text-red-500" />;
  if (safeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || safeName.endsWith(".docx")) return <FaFileWord className="text-blue-600" />;
  if (safeType.startsWith("image/")) return <FaFileImage className="text-green-500" />;
  if (safeType === "text/plain" || safeName.endsWith(".txt")) return <FaFileAlt className="text-gray-600" />;
  if (safeType === "application/zip" || safeName.endsWith(".zip")) return <FaFileArchive className="text-yellow-600" />;
  return <FaFileAlt className="text-gray-400" />;
};


function formatSize(bytes) {
  if (bytes == null || isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes < 1024 * 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  return `${(bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`;
}

function formatDate(date) {
  if (!date) return "";
  let d = date;
  // Accept Firestore Timestamp, string, number, or Date
  if (typeof d === "object" && d && typeof d.toDate === "function") {
    d = d.toDate();
  } else if (typeof d === "string" || typeof d === "number") {
    d = new Date(d);
  }
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

const FileList = ({ files, onPreview, onDelete, onDownload, onRename }) => (
  <ul className="divide-y divide-gray-200">
    {files.map(file => (
  <li key={file.id} data-file-id={file.id} className="py-1 group hover:bg-gray-50 rounded transition">
        <div className="flex items-center w-full min-h-[32px]">
          <span className="file-icon" style={{ fontSize: 18 }}>{getFileIcon(file.type, file.name)}</span>
          <div
            className="file-name-container"
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              position: 'relative',
              cursor: 'pointer',
              flex: 1,
              marginLeft: 6,
              marginRight: 6,
              fontSize: 15,
            }}
            title={file.name}
            onMouseEnter={e => {
              const tooltip = document.createElement('div');
              tooltip.className = 'file-name-tooltip';
              tooltip.innerText = file.name;
              tooltip.style.position = 'fixed';
              tooltip.style.background = '#fff';
              tooltip.style.border = '1px solid #ccc';
              tooltip.style.padding = '4px 8px';
              tooltip.style.zIndex = 9999;
              tooltip.style.top = `${e.clientY + 8}px`;
              tooltip.style.left = `${e.clientX + 8}px`;
              document.body.appendChild(tooltip);
              e.target._tooltip = tooltip;
            }}
            onMouseLeave={e => {
              if (e.target._tooltip) {
                document.body.removeChild(e.target._tooltip);
                e.target._tooltip = null;
              }
            }}
          >
            {file.name}
          </div>
          <div className="flex gap-1">
            <button title="Preview" className="p-1 rounded hover:bg-blue-100 transition" onClick={() => onPreview(file)}>
              <Eye className="w-4 h-4 text-blue-600" />
            </button>
            <button title="Rename" className="p-1 rounded hover:bg-yellow-100 transition" onClick={() => onRename(file)}>
              <Edit2 className="w-4 h-4 text-yellow-600" />
            </button>
            <button title="Download" className="p-1 rounded hover:bg-green-100 transition" onClick={() => onDownload(file)}>
              <Download className="w-4 h-4 text-green-600" />
            </button>
            <button title="Delete" className="p-1 rounded hover:bg-red-100 transition" onClick={() => onDelete(file)}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>
        <div className="w-full">
          <div className="flex flex-row gap-1 items-center text-xs text-gray-500 mt-0.5" style={{ marginLeft: '30px', fontSize: 13 }}>
            <span className="whitespace-nowrap">Size: {formatSize(file.size ?? 0)}</span>
            <span className="whitespace-nowrap ml-2">Modified: {formatDate(file.updatedAt || file.modifiedAt || file.createdAt)}</span>
          </div>
        </div>
      </li>
    ))}
    {files.length === 0 && <li className="text-gray-400 py-4 text-center">No files in this folder.</li>}
  </ul>
);

export default FileList;
